import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { 
  UserCheck, Plus, Search, Filter, MessageSquare, Clock, MapPin, 
  MoreVertical, Phone, Info, X, User, CheckCircle2, LogOut, 
  Mail, Calendar, RefreshCw, AlertCircle, Trash2, Building,
  ShieldCheck, AlertTriangle, ShieldAlert
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../../components/common/Button";
import InputField from "../../components/common/InputField";
import PaginationFooter from "../../components/common/PaginationFooter";
import { 
  VisitorLogItem, 
  VisitorKPIs,
  CreateVisitorPayload,
  VisitorQueryParams 
} from "../../services/visitorService";
import { 
  useVisitorsQuery, 
  useCreateVisitorMutation, 
  useCheckInVisitorMutation, 
  useCheckOutVisitorMutation,
  useUpdateVisitorMutation,
  useDeleteVisitorMutation 
} from "../../hooks/queries/useVisitorQueries";

const GlassCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`cht-bg-white/90 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.03)] ${className}`}>
    {children}
  </div>
);

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children }) => {
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="cht-fixed cht-inset-0 cht-z-[10000] cht-flex cht-items-center cht-justify-center cht-p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="cht-absolute cht-inset-0 cht-bg-slate-950/50 cht-backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
            className="cht-relative cht-w-full cht-max-w-lg cht-bg-white cht-rounded-[32px] cht-p-8 cht-shadow-[0_25px_70px_rgba(0,0,0,0.15)] cht-max-h-[90vh] cht-overflow-y-auto cht-border cht-border-gray-100"
          >
            <div className="cht-flex cht-justify-between cht-items-center cht-mb-6 cht-pb-4 cht-border-b cht-border-gray-100">
              <h2 className="cht-text-2xl cht-font-black cht-text-[#1e293b] cht-tracking-tight">{title}</h2>
              <button 
                onClick={onClose} 
                className="cht-p-2.5 cht-bg-gray-50 cht-rounded-2xl cht-text-gray-400 hover:cht-text-gray-700 hover:cht-bg-gray-100 transition-colors"
                aria-label="Close modal"
              >
                <X size={18} />
              </button>
            </div>
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

const PURPOSE_OPTIONS = [
  "Course Enquiry",
  "Business Meeting",
  "Interview",
  "Maintenance",
  "Delivery",
  "Consultation",
  "Student Support",
  "Executive Partner",
  "Vendor / Supplier",
  "Other"
];

const SuperAdminVisitors: React.FC = () => {
  const createMutation = useCreateVisitorMutation();
  const checkInMutation = useCheckInVisitorMutation();
  const checkOutMutation = useCheckOutVisitorMutation();
  const updateMutation = useUpdateVisitorMutation();
  const deleteMutation = useDeleteVisitorMutation();

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [purposeFilter, setPurposeFilter] = useState("ALL");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Stable query parameters ensure identical cache keys on navigation remount
  const queryParams: VisitorQueryParams = useMemo(() => {
    const p: VisitorQueryParams = {
      page: currentPage,
      page_size: pageSize,
    };
    const trimmed = searchQuery.trim();
    if (trimmed) p.search = trimmed;
    if (statusFilter && statusFilter !== "ALL") p.status = statusFilter;
    if (purposeFilter && purposeFilter !== "ALL") p.purpose = purposeFilter;
    if (startDate) p.start_date = startDate;
    if (endDate) p.end_date = endDate;
    return p;
  }, [currentPage, pageSize, searchQuery, statusFilter, purposeFilter, startDate, endDate]);

  const {
    data: visitorsData,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useVisitorsQuery(queryParams);

  const visitors = visitorsData?.data || [];
  const totalCount = visitorsData?.count || 0;
  const kpis: VisitorKPIs = visitorsData?.analytics || {
    total_visitors: 0,
    today_visitors: 0,
    currently_onsite: 0,
    active_enquiries: 0,
    total_checked_out: 0,
    total_cancelled: 0,
  };
  const errorMsg = isError
    ? (error as any)?.response?.data?.detail || "Failed to load facility access logs."
    : null;

  // UI / Modal States
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "view" | "delete">("add");
  const [selectedVisitor, setSelectedVisitor] = useState<VisitorLogItem | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form State
  const [formData, setFormData] = useState<CreateVisitorPayload>({
    full_name: "",
    phone_number: "",
    email: "",
    purpose: "Business Meeting",
    whom_to_see: "",
    status: "CHECKED_IN",
    notes: "",
  });

  // Filter handlers
  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handlePurposeFilterChange = (purpose: string) => {
    setPurposeFilter(purpose);
    setCurrentPage(1);
  };

  const openModal = (mode: "add" | "view" | "delete", visitor: VisitorLogItem | null = null) => {
    setModalMode(mode);
    setSelectedVisitor(visitor);
    if (mode === "add") {
      setFormData({
        full_name: "",
        phone_number: "",
        email: "",
        purpose: "Business Meeting",
        whom_to_see: "",
        status: "CHECKED_IN",
        notes: "",
      });
    }
    setIsModalOpen(true);
    setActiveDropdown(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedVisitor(null);
    setIsSubmitting(false);
  };

  // Create record
  const handleAddVisitor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.full_name.trim() || !formData.phone_number.trim()) {
      alert("Please provide the visitor's full name and phone number.");
      return;
    }

    setIsSubmitting(true);
    try {
      await createMutation.mutateAsync(formData);
      closeModal();
    } catch (err: any) {
      console.error("Failed to create visitor entry:", err);
      alert(err?.response?.data?.detail || "Could not log visitor. Please check your inputs.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Status transitions
  const handleCheckIn = async (id: number) => {
    try {
      const updated = await checkInMutation.mutateAsync(id);
      if (selectedVisitor?.id === id) setSelectedVisitor(updated);
      setActiveDropdown(null);
    } catch (err: any) {
      console.error("Failed to check in visitor:", err);
      alert("Failed to check in visitor.");
    }
  };

  const handleCheckOut = async (id: number) => {
    try {
      const updated = await checkOutMutation.mutateAsync(id);
      if (selectedVisitor?.id === id) setSelectedVisitor(updated);
      setActiveDropdown(null);
    } catch (err: any) {
      console.error("Failed to check out visitor:", err);
      alert("Failed to check out visitor.");
    }
  };

  const handleCancelStatus = async (id: number) => {
    try {
      const updated = await updateMutation.mutateAsync({ id, payload: { status: "CANCELLED" } });
      if (selectedVisitor?.id === id) setSelectedVisitor(updated);
      setActiveDropdown(null);
    } catch (err: any) {
      console.error("Failed to cancel visitor entry:", err);
      alert("Failed to cancel visitor entry.");
    }
  };

  // Delete record (SuperAdmin privilege)
  const handleDeleteVisitor = async () => {
    if (!selectedVisitor) return;
    setIsSubmitting(true);
    try {
      await deleteMutation.mutateAsync(selectedVisitor.id);
      closeModal();
    } catch (err: any) {
      console.error("Failed to delete visitor log:", err);
      alert(err?.response?.data?.detail || "Failed to delete record.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDuration = (mins?: number | null) => {
    if (mins === undefined || mins === null) return "N/A";
    if (mins < 1) return "< 1 min";
    if (mins < 60) return `${mins} min${mins > 1 ? "s" : ""}`;
    const hrs = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    return `${hrs} hr${hrs > 1 ? "s" : ""} ${remainingMins}m`;
  };

  const formatTime = (isoString?: string | null) => {
    if (!isoString) return "N/A";
    try {
      return new Date(isoString).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
      return isoString;
    }
  };

  const formatDate = (isoString?: string | null) => {
    if (!isoString) return "N/A";
    try {
      return new Date(isoString).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
    } catch {
      return isoString;
    }
  };

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-700">
      {/* Top Header */}
      <header className="cht-flex cht-flex-col md:cht-flex-row cht-items-start md:cht-items-center cht-justify-between cht-gap-4">
        <div>
          <div className="cht-flex cht-items-center cht-gap-2.5">
            <h1 className="cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight">
              SuperAdmin Visitor & Access Log
            </h1>
            <span className="cht-px-2.5 cht-py-0.5 cht-bg-indigo-50 cht-text-indigo-700 cht-border cht-border-indigo-200/60 cht-text-xs cht-font-black cht-rounded-full cht-flex cht-items-center cht-gap-1">
              <ShieldCheck size={13} /> Facility Audit
            </span>
          </div>
          <p className="cht-text-gray-500 cht-font-medium cht-text-sm cht-mt-1">
            Master facility access oversight, high-profile visitor logs, front desk staff audit, and security records.
          </p>
        </div>
        <div className="cht-flex cht-items-center cht-gap-3">
          <Button
            variant="outline"
            leftIcon={<RefreshCw size={16} className={isFetching ? "cht-animate-spin" : ""} />}
            onClick={() => refetch()}
            className="cht-rounded-2xl"
          >
            Refresh
          </Button>
          <Button
            variant="primary"
            leftIcon={<Plus size={18} />}
            onClick={() => openModal("add")}
            className="cht-rounded-2xl"
          >
            Register Visitor Entry
          </Button>
        </div>
      </header>

      {/* KPI Deck */}
      <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 lg:cht-grid-cols-5 cht-gap-5">
        {[
          {
            label: "Today's Entries",
            value: kpis.today_visitors,
            icon: UserCheck,
            colorClass: "cht-bg-blue-50 cht-text-blue-600",
            sub: "Checked today",
          },
          {
            label: "Currently Onsite",
            value: kpis.currently_onsite,
            icon: MapPin,
            colorClass: "cht-bg-emerald-50 cht-text-emerald-600",
            sub: "Active in building",
          },
          {
            label: "Active Enquiries",
            value: kpis.active_enquiries,
            icon: MessageSquare,
            colorClass: "cht-bg-purple-50 cht-text-purple-600",
            sub: "Consultations & lobby",
          },
          {
            label: "Total Checked Out",
            value: kpis.total_checked_out,
            icon: Clock,
            colorClass: "cht-bg-amber-50 cht-text-amber-600",
            sub: "Completed visits",
          },
          {
            label: "Total Facility Visits",
            value: kpis.total_visitors,
            icon: Building,
            colorClass: "cht-bg-indigo-50 cht-text-indigo-600",
            sub: "All-time database count",
          },
        ].map((stat, i) => (
          <GlassCard key={i} className="cht-p-5 cht-rounded-[24px] cht-flex cht-items-center cht-gap-3.5">
            <div className={`cht-p-3.5 ${stat.colorClass} cht-rounded-2xl cht-shrink-0`}>
              <stat.icon size={22} />
            </div>
            <div>
              <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">
                {stat.label}
              </p>
              <h4 className="cht-text-xl cht-font-black cht-text-[#1e293b]">{stat.value}</h4>
              <p className="cht-text-[10px] cht-font-medium cht-text-gray-400">{stat.sub}</p>
            </div>
          </GlassCard>
        ))}
      </div>

      {/* Filter Deck */}
      <div className="cht-space-y-4">
        {/* Status Pills & Date Range */}
        <div className="cht-flex cht-flex-col lg:cht-flex-row cht-items-start lg:cht-items-center cht-justify-between cht-gap-4 cht-bg-white cht-p-4 cht-rounded-[24px] cht-border cht-border-gray-100 cht-shadow-sm">
          {/* Status Tabs */}
          <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-2">
            {[
              { id: "ALL", label: "All Records" },
              { id: "CHECKED_IN", label: "Checked In" },
              { id: "ENQUIRY", label: "Enquiries" },
              { id: "CHECKED_OUT", label: "Checked Out" },
              { id: "CANCELLED", label: "Cancelled" },
            ].map((pill) => {
              const isActive = statusFilter === pill.id;
              return (
                <button
                  key={pill.id}
                  onClick={() => handleStatusFilterChange(pill.id)}
                  className={`cht-px-4 cht-py-2 cht-rounded-xl cht-text-xs cht-font-black cht-transition-all ${
                    isActive
                      ? "cht-bg-[#1e293b] cht-text-white cht-shadow-sm"
                      : "cht-bg-gray-50 cht-text-gray-600 hover:cht-bg-gray-100"
                  }`}
                >
                  {pill.label}
                </button>
              );
            })}
          </div>

          {/* Date Range Selector */}
          <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-3">
            <div className="cht-flex cht-items-center cht-gap-2 cht-text-xs cht-font-bold cht-text-gray-500">
              <Calendar size={15} className="cht-text-[#3e57da]" />
              <span>Date Filter:</span>
            </div>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setCurrentPage(1);
              }}
              className="cht-bg-gray-50 cht-border cht-border-gray-200 cht-rounded-xl cht-py-2 cht-px-3 cht-text-xs cht-font-bold cht-text-gray-700 focus:cht-ring-2 focus:cht-ring-[#3e57da]/10"
              title="From Date"
            />
            <span className="cht-text-gray-400 cht-text-xs cht-font-bold">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setCurrentPage(1);
              }}
              className="cht-bg-gray-50 cht-border cht-border-gray-200 cht-rounded-xl cht-py-2 cht-px-3 cht-text-xs cht-font-bold cht-text-gray-700 focus:cht-ring-2 focus:cht-ring-[#3e57da]/10"
              title="To Date"
            />
            {(startDate || endDate) && (
              <button
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                  setCurrentPage(1);
                }}
                className="cht-p-2 cht-bg-rose-50 hover:cht-bg-rose-100 cht-text-rose-500 cht-rounded-xl cht-transition-colors"
                title="Clear date filter"
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>

        {/* Search Bar & Purpose Selector */}
        <div className="cht-flex cht-flex-col sm:cht-flex-row cht-items-center cht-gap-4 cht-bg-white cht-p-4 cht-rounded-[24px] cht-border cht-border-gray-100 cht-shadow-sm">
          <div className="cht-relative cht-flex-1 cht-w-full">
            <Search className="cht-absolute cht-left-4 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by visitor name, phone, email, host or ID (e.g. VST-0001)..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="cht-w-full cht-bg-gray-50 cht-border-none cht-rounded-xl cht-py-3.5 cht-pl-12 cht-pr-10 cht-text-sm cht-font-bold focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => handleSearchChange("")}
                className="cht-absolute cht-right-3 cht-top-1/2 -cht-translate-y-1/2 cht-p-1 cht-text-gray-400 hover:cht-text-gray-600"
              >
                <X size={16} />
              </button>
            )}
          </div>

          <div className="cht-flex cht-items-center cht-gap-2 cht-w-full sm:cht-w-auto">
            <Filter size={16} className="cht-text-gray-400" />
            <select
              value={purposeFilter}
              onChange={(e) => handlePurposeFilterChange(e.target.value)}
              className="cht-bg-gray-50 cht-border-none cht-rounded-xl cht-py-3 cht-px-4 cht-text-xs cht-font-bold cht-text-gray-700 focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 cht-cursor-pointer"
            >
              <option value="ALL">All Visit Purposes</option>
              {PURPOSE_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {errorMsg && (
        <div className="cht-p-4 cht-rounded-2xl cht-bg-rose-50 cht-border cht-border-rose-100 cht-flex cht-items-center cht-gap-3 cht-text-rose-700">
          <AlertCircle size={18} />
          <span className="cht-text-sm cht-font-bold">{errorMsg}</span>
        </div>
      )}

      {/* Facility Visitor Master Table */}
      <GlassCard className="cht-rounded-[36px] cht-overflow-hidden">
        <div className="cht-overflow-x-auto">
          <table className="cht-w-full cht-border-collapse">
            <thead>
              <tr className="cht-border-b cht-border-gray-100 cht-bg-gray-50/50">
                <th className="cht-px-6 cht-py-4 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                  Visitor Identity
                </th>
                <th className="cht-px-6 cht-py-4 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                  Purpose & Host
                </th>
                <th className="cht-px-6 cht-py-4 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                  Contact
                </th>
                <th className="cht-px-6 cht-py-4 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                  Check-in / Out
                </th>
                <th className="cht-px-6 cht-py-4 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                  Logged By (Staff)
                </th>
                <th className="cht-px-6 cht-py-4 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                  Status
                </th>
                <th className="cht-px-6 cht-py-4 cht-text-right cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="cht-divide-y cht-divide-gray-50">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={`skeleton-${idx}`} className="cht-animate-pulse">
                    <td className="cht-px-6 cht-py-5">
                      <div className="cht-h-4 cht-bg-gray-200 cht-rounded-md cht-w-32 cht-mb-2" />
                      <div className="cht-h-3 cht-bg-gray-100 cht-rounded-md cht-w-20" />
                    </td>
                    <td className="cht-px-6 cht-py-5">
                      <div className="cht-h-4 cht-bg-gray-200 cht-rounded-md cht-w-28 cht-mb-2" />
                      <div className="cht-h-3 cht-bg-gray-100 cht-rounded-md cht-w-24" />
                    </td>
                    <td className="cht-px-6 cht-py-5">
                      <div className="cht-h-4 cht-bg-gray-200 cht-rounded-md cht-w-24" />
                    </td>
                    <td className="cht-px-6 cht-py-5">
                      <div className="cht-h-4 cht-bg-gray-200 cht-rounded-md cht-w-20 cht-mb-1" />
                      <div className="cht-h-3 cht-bg-gray-100 cht-rounded-md cht-w-16" />
                    </td>
                    <td className="cht-px-6 cht-py-5">
                      <div className="cht-h-4 cht-bg-gray-200 cht-rounded-md cht-w-24" />
                    </td>
                    <td className="cht-px-6 cht-py-5">
                      <div className="cht-h-6 cht-bg-gray-200 cht-rounded-full cht-w-20" />
                    </td>
                    <td className="cht-px-6 cht-py-5 cht-text-right">
                      <div className="cht-h-8 cht-w-8 cht-bg-gray-200 cht-rounded-xl cht-inline-block" />
                    </td>
                  </tr>
                ))
              ) : visitors.length === 0 ? (
                <tr>
                  <td colSpan={7} className="cht-py-20 cht-text-center">
                    <div className="cht-w-16 cht-h-16 cht-bg-gray-50 cht-rounded-2xl cht-flex cht-items-center cht-justify-center cht-mx-auto cht-mb-4 cht-text-gray-300">
                      <UserCheck size={32} />
                    </div>
                    <h3 className="cht-text-lg cht-font-black cht-text-gray-800">No visitors recorded</h3>
                    <p className="cht-text-sm cht-text-gray-400 cht-mt-1">
                      {searchQuery || statusFilter !== "ALL" || startDate
                        ? "No results matching current filters."
                        : "Register new facility visitors using the top action button."}
                    </p>
                  </td>
                </tr>
              ) : (
                visitors.map((visitor) => {
                  const isCheckedIn = visitor.status === "CHECKED_IN";
                  const isEnquiry = visitor.status === "ENQUIRY";
                  const isCheckedOut = visitor.status === "CHECKED_OUT";

                  return (
                    <tr
                      key={visitor.id}
                      className="hover:cht-bg-gray-50/70 cht-transition-colors group"
                    >
                      {/* Identity */}
                      <td className="cht-px-6 cht-py-5">
                        <div className="cht-flex cht-items-center cht-gap-3">
                          <div className="cht-w-10 cht-h-10 cht-rounded-2xl cht-bg-gradient-to-br cht-from-[#1e293b]/10 cht-to-[#3e57da]/10 cht-text-[#1e293b] cht-flex cht-items-center cht-justify-center cht-font-black cht-text-xs cht-shrink-0">
                            {visitor.full_name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="cht-flex cht-items-center cht-gap-2">
                              <h4 className="cht-text-sm cht-font-black cht-text-gray-900">
                                {visitor.full_name}
                              </h4>
                              {visitor.visitor_id && (
                                <span className="cht-text-[10px] cht-font-mono cht-font-black cht-px-2 cht-py-0.5 cht-bg-gray-100 cht-text-gray-600 cht-rounded-md">
                                  {visitor.visitor_id}
                                </span>
                              )}
                            </div>
                            <span className="cht-text-[11px] cht-text-gray-400 cht-font-medium cht-block cht-mt-0.5">
                              {formatDate(visitor.date)}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Purpose & Host */}
                      <td className="cht-px-6 cht-py-5">
                        <div className="cht-space-y-0.5">
                          <div className="cht-flex cht-items-center cht-gap-1.5 cht-text-xs cht-font-bold cht-text-gray-800">
                            <Info size={14} className="cht-text-[#3e57da] cht-shrink-0" />
                            <span>{visitor.purpose}</span>
                          </div>
                          {visitor.whom_to_see ? (
                            <div className="cht-flex cht-items-center cht-gap-1 cht-text-[11px] cht-text-gray-500 cht-font-medium">
                              <Building size={12} className="cht-text-gray-400" />
                              <span>Host: {visitor.whom_to_see}</span>
                            </div>
                          ) : (
                            <span className="cht-text-[11px] cht-text-gray-400 cht-italic">General Visitor</span>
                          )}
                        </div>
                      </td>

                      {/* Contact Info */}
                      <td className="cht-px-6 cht-py-5">
                        <div className="cht-space-y-1">
                          <div className="cht-flex cht-items-center cht-gap-1.5 cht-text-xs cht-font-bold cht-text-gray-700">
                            <Phone size={13} className="cht-text-emerald-500" />
                            <a
                              href={`tel:${visitor.phone_number}`}
                              className="hover:cht-text-[#3e57da] hover:cht-underline"
                            >
                              {visitor.phone_number}
                            </a>
                          </div>
                          {visitor.email && (
                            <div className="cht-flex cht-items-center cht-gap-1.5 cht-text-[11px] cht-text-gray-400">
                              <Mail size={12} className="cht-text-gray-400" />
                              <span className="cht-truncate cht-max-w-[140px]">{visitor.email}</span>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Timing / Duration */}
                      <td className="cht-px-6 cht-py-5">
                        <div className="cht-space-y-0.5">
                          <div className="cht-flex cht-items-center cht-gap-1 cht-text-xs cht-font-bold cht-text-gray-800">
                            <Clock size={13} className="cht-text-amber-500" />
                            <span>{formatTime(visitor.check_in_time)}</span>
                          </div>
                          <span className="cht-text-[11px] cht-text-gray-400 cht-font-medium">
                            {isCheckedOut
                              ? `Out: ${formatTime(visitor.check_out_time)} (${formatDuration(visitor.duration_minutes)})`
                              : `Onsite: ${formatDuration(visitor.duration_minutes)}`}
                          </span>
                        </div>
                      </td>

                      {/* Logged by staff */}
                      <td className="cht-px-6 cht-py-5">
                        <span className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-2.5 cht-py-1 cht-bg-gray-100 cht-text-gray-700 cht-rounded-lg cht-text-xs cht-font-bold">
                          <User size={12} className="cht-text-gray-500" />
                          {visitor.logged_by_name || "Superadmin"}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="cht-px-6 cht-py-5">
                        <span
                          className={`cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3 cht-py-1.5 cht-rounded-full cht-text-[10px] cht-font-black cht-uppercase cht-tracking-wider ${
                            isCheckedIn
                              ? "cht-bg-emerald-100 cht-text-emerald-800"
                              : isEnquiry
                              ? "cht-bg-purple-100 cht-text-purple-800"
                              : isCheckedOut
                              ? "cht-bg-gray-100 cht-text-gray-700"
                              : "cht-bg-rose-100 cht-text-rose-800"
                          }`}
                        >
                          <span
                            className={`cht-w-1.5 cht-h-1.5 cht-rounded-full ${
                              isCheckedIn
                                ? "cht-bg-emerald-600 cht-animate-pulse"
                                : isEnquiry
                                ? "cht-bg-purple-600"
                                : isCheckedOut
                                ? "cht-bg-gray-500"
                                : "cht-bg-rose-600"
                            }`}
                          />
                          {visitor.status_display || visitor.status.replace("_", " ")}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="cht-px-6 cht-py-5 cht-text-right">
                        <div className="cht-relative cht-inline-block">
                          <button
                            onClick={() =>
                              setActiveDropdown(activeDropdown === visitor.id ? null : visitor.id)
                            }
                            className="cht-w-9 cht-h-9 cht-flex cht-items-center cht-justify-center cht-rounded-xl cht-text-gray-400 hover:cht-bg-gray-100 hover:cht-text-gray-900 transition-colors"
                            aria-label="Actions"
                          >
                            <MoreVertical size={18} />
                          </button>

                          <AnimatePresence>
                            {activeDropdown === visitor.id && (
                              <motion.div
                                initial={{ opacity: 0, y: 8, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 8, scale: 0.95 }}
                                transition={{ duration: 0.15 }}
                                className="cht-absolute cht-right-0 cht-mt-2 cht-w-56 cht-bg-white cht-rounded-2xl cht-shadow-[0_20px_60px_rgba(0,0,0,0.12)] cht-border cht-border-gray-100 cht-p-2 cht-z-[999]"
                              >
                                <button
                                  onClick={() => openModal("view", visitor)}
                                  className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-3.5 cht-py-2.5 cht-text-xs cht-font-bold cht-text-gray-700 hover:cht-bg-gray-50 cht-rounded-xl transition-colors"
                                >
                                  <Info size={15} className="cht-text-[#3e57da]" />
                                  View Audit Dossier
                                </button>

                                <div className="cht-h-px cht-bg-gray-100 cht-my-1" />

                                {!isCheckedIn && (
                                  <button
                                    onClick={() => handleCheckIn(visitor.id)}
                                    className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-3.5 cht-py-2.5 cht-text-xs cht-font-bold cht-text-emerald-700 hover:cht-bg-emerald-50 cht-rounded-xl transition-colors"
                                  >
                                    <CheckCircle2 size={15} className="cht-text-emerald-600" />
                                    Mark Checked In
                                  </button>
                                )}

                                {!isCheckedOut && (
                                  <button
                                    onClick={() => handleCheckOut(visitor.id)}
                                    className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-3.5 cht-py-2.5 cht-text-xs cht-font-bold cht-text-amber-700 hover:cht-bg-amber-50 cht-rounded-xl transition-colors"
                                  >
                                    <LogOut size={15} className="cht-text-amber-600" />
                                    Mark Checked Out
                                  </button>
                                )}

                                {visitor.status !== "CANCELLED" && (
                                  <button
                                    onClick={() => handleCancelStatus(visitor.id)}
                                    className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-3.5 cht-py-2.5 cht-text-xs cht-font-bold cht-text-gray-600 hover:cht-bg-gray-50 cht-rounded-xl transition-colors"
                                  >
                                    <X size={15} className="cht-text-gray-400" />
                                    Cancel Visit Entry
                                  </button>
                                )}

                                <div className="cht-h-px cht-bg-gray-100 cht-my-1" />

                                <button
                                  onClick={() => openModal("delete", visitor)}
                                  className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-3.5 cht-py-2.5 cht-text-xs cht-font-bold cht-text-rose-600 hover:cht-bg-rose-50 cht-rounded-xl transition-colors"
                                >
                                  <Trash2 size={15} />
                                  Delete Record
                                </button>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Server Pagination Footer */}
        <PaginationFooter
          currentPage={currentPage}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={(page) => setCurrentPage(page)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          isLoading={isLoading}
        />
      </GlassCard>

      {/* Dropdown backdrop closer */}
      {activeDropdown && (
        <div
          className="cht-fixed cht-inset-0 cht-z-[90]"
          onClick={() => setActiveDropdown(null)}
        />
      )}

      {/* Modal: Register Visitor Entry */}
      <Modal
        isOpen={isModalOpen && modalMode === "add"}
        onClose={closeModal}
        title="Register Facility Access Entry"
      >
        <form onSubmit={handleAddVisitor} className="cht-space-y-4">
          <InputField
            label="Visitor Full Name *"
            placeholder="E.g. Dr. Jane Smith"
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            leftIcon={<User size={17} />}
            required
          />

          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-4">
            <InputField
              label="Phone Number *"
              placeholder="08012345678"
              value={formData.phone_number}
              onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
              leftIcon={<Phone size={17} />}
              required
            />
            <InputField
              label="Email Address"
              placeholder="visitor@organization.com"
              type="email"
              value={formData.email || ""}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              leftIcon={<Mail size={17} />}
            />
          </div>

          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-4">
            <div className="cht-flex cht-flex-col cht-gap-1.5">
              <label className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                Purpose of Visit
              </label>
              <select
                value={formData.purpose}
                onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                className="cht-w-full cht-bg-gray-50 cht-border cht-border-gray-200 cht-rounded-2xl cht-py-3 cht-px-3.5 cht-text-xs cht-font-bold cht-text-gray-700 focus:cht-ring-2 focus:cht-ring-[#3e57da]/15"
              >
                {PURPOSE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <InputField
              label="Host / Executive Contact"
              placeholder="Whom to see / Department"
              value={formData.whom_to_see || ""}
              onChange={(e) => setFormData({ ...formData, whom_to_see: e.target.value })}
              leftIcon={<Building size={17} />}
            />
          </div>

          {/* Initial Status */}
          <div className="cht-flex cht-flex-col cht-gap-1.5">
            <label className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Entry Status
            </label>
            <div className="cht-grid cht-grid-cols-2 cht-gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, status: "CHECKED_IN" })}
                className={`cht-py-2.5 cht-rounded-xl cht-text-xs cht-font-black cht-transition-all ${
                  formData.status === "CHECKED_IN"
                    ? "cht-bg-emerald-100 cht-text-emerald-800 cht-border cht-border-emerald-200"
                    : "cht-bg-gray-50 cht-text-gray-600 hover:cht-bg-gray-100"
                }`}
              >
                Checked In Onsite
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, status: "ENQUIRY" })}
                className={`cht-py-2.5 cht-rounded-xl cht-text-xs cht-font-black cht-transition-all ${
                  formData.status === "ENQUIRY"
                    ? "cht-bg-purple-100 cht-text-purple-800 cht-border cht-border-purple-200"
                    : "cht-bg-gray-50 cht-text-gray-600 hover:cht-bg-gray-100"
                }`}
              >
                Enquiry / Front Lobby
              </button>
            </div>
          </div>

          {/* Notes */}
          <div className="cht-flex cht-flex-col cht-gap-1.5">
            <label className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Executive Notes / Clearance
            </label>
            <textarea
              rows={3}
              placeholder="Meeting agenda, special clearance, remarks..."
              value={formData.notes || ""}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="cht-w-full cht-bg-gray-50 cht-border cht-border-gray-200 cht-rounded-2xl cht-p-3 cht-text-xs cht-font-medium cht-text-gray-800 focus:cht-ring-2 focus:cht-ring-[#3e57da]/15"
            />
          </div>

          <div className="cht-pt-4 cht-flex cht-justify-end cht-gap-3 cht-border-t cht-border-gray-100">
            <Button variant="ghost" type="button" onClick={closeModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Registering..." : "Confirm & Register"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal: View Audit Dossier */}
      <Modal
        isOpen={isModalOpen && modalMode === "view"}
        onClose={closeModal}
        title="Facility Access Audit Dossier"
      >
        {selectedVisitor && (
          <div className="cht-space-y-6">
            <div className="cht-flex cht-items-center cht-gap-5 cht-bg-gray-50 cht-p-5 cht-rounded-2xl cht-border cht-border-gray-100">
              <div className="cht-w-16 cht-h-16 cht-bg-[#1e293b] cht-text-white cht-rounded-2xl cht-shadow-sm cht-flex cht-items-center cht-justify-center cht-text-2xl cht-font-black">
                {selectedVisitor.full_name.charAt(0).toUpperCase()}
              </div>
              <div className="cht-flex-1">
                <div className="cht-flex cht-items-center cht-gap-2">
                  <h3 className="cht-text-xl cht-font-black cht-text-gray-900">
                    {selectedVisitor.full_name}
                  </h3>
                  {selectedVisitor.visitor_id && (
                    <span className="cht-px-2 cht-py-0.5 cht-bg-white cht-border cht-border-gray-200 cht-rounded-md cht-text-xs cht-font-mono cht-font-bold text-gray-700">
                      {selectedVisitor.visitor_id}
                    </span>
                  )}
                </div>
                <div className="cht-flex cht-items-center cht-gap-2 cht-mt-2">
                  <span
                    className={`cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-black cht-uppercase cht-tracking-wider ${
                      selectedVisitor.status === "CHECKED_IN"
                        ? "cht-bg-emerald-100 cht-text-emerald-800"
                        : selectedVisitor.status === "ENQUIRY"
                        ? "cht-bg-purple-100 cht-text-purple-800"
                        : selectedVisitor.status === "CHECKED_OUT"
                        ? "cht-bg-gray-100 cht-text-gray-700"
                        : "cht-bg-rose-100 cht-text-rose-800"
                    }`}
                  >
                    {selectedVisitor.status_display || selectedVisitor.status.replace("_", " ")}
                  </span>
                  <span className="cht-text-xs cht-text-gray-400">
                    {formatDate(selectedVisitor.date)}
                  </span>
                </div>
              </div>
            </div>

            {/* Audit Metadata Grid */}
            <div className="cht-grid cht-grid-cols-2 cht-gap-3">
              <div className="cht-p-3.5 cht-bg-gray-50/50 cht-border cht-border-gray-100 cht-rounded-xl">
                <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-mb-1">
                  Phone Number
                </p>
                <p className="cht-text-xs cht-font-bold cht-text-gray-800 cht-flex cht-items-center cht-gap-1.5">
                  <Phone size={13} className="cht-text-emerald-500" />
                  {selectedVisitor.phone_number}
                </p>
              </div>

              <div className="cht-p-3.5 cht-bg-gray-50/50 cht-border cht-border-gray-100 cht-rounded-xl">
                <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-mb-1">
                  Email
                </p>
                <p className="cht-text-xs cht-font-bold cht-text-gray-800 cht-truncate">
                  {selectedVisitor.email || "Not provided"}
                </p>
              </div>

              <div className="cht-p-3.5 cht-bg-gray-50/50 cht-border cht-border-gray-100 cht-rounded-xl">
                <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-mb-1">
                  Visit Purpose
                </p>
                <p className="cht-text-xs cht-font-bold cht-text-gray-800">
                  {selectedVisitor.purpose}
                </p>
              </div>

              <div className="cht-p-3.5 cht-bg-gray-50/50 cht-border cht-border-gray-100 cht-rounded-xl">
                <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-mb-1">
                  Host / Whom to See
                </p>
                <p className="cht-text-xs cht-font-bold cht-text-gray-800">
                  {selectedVisitor.whom_to_see || "Facility Front Desk"}
                </p>
              </div>

              <div className="cht-p-3.5 cht-bg-gray-50/50 cht-border cht-border-gray-100 cht-rounded-xl">
                <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-mb-1">
                  Check-in Timestamp
                </p>
                <p className="cht-text-xs cht-font-bold cht-text-gray-800 cht-flex cht-items-center cht-gap-1">
                  <Clock size={12} className="cht-text-amber-500" />
                  {formatTime(selectedVisitor.check_in_time)}
                </p>
              </div>

              <div className="cht-p-3.5 cht-bg-gray-50/50 cht-border cht-border-gray-100 cht-rounded-xl">
                <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-mb-1">
                  Check-out / Duration
                </p>
                <p className="cht-text-xs cht-font-bold cht-text-gray-800">
                  {selectedVisitor.check_out_time
                    ? `${formatTime(selectedVisitor.check_out_time)} (${formatDuration(selectedVisitor.duration_minutes)})`
                    : `Active (${formatDuration(selectedVisitor.duration_minutes)})`}
                </p>
              </div>

              <div className="cht-col-span-2 cht-p-3.5 cht-bg-gray-50/50 cht-border cht-border-gray-100 cht-rounded-xl">
                <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-mb-1">
                  Logged By (Staff Auditor)
                </p>
                <p className="cht-text-xs cht-font-bold cht-text-gray-800">
                  {selectedVisitor.logged_by_name || "Superadmin"}
                </p>
              </div>

              {selectedVisitor.notes && (
                <div className="cht-col-span-2 cht-p-3.5 cht-bg-gray-50/50 cht-border cht-border-gray-100 cht-rounded-xl">
                  <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-mb-1">
                    Notes & Remarks
                  </p>
                  <p className="cht-text-xs cht-text-gray-700 cht-whitespace-pre-line">
                    {selectedVisitor.notes}
                  </p>
                </div>
              )}
            </div>

            {/* Quick Action Footer */}
            <div className="cht-flex cht-items-center cht-justify-between cht-pt-4 cht-border-t cht-border-gray-100">
              <Button
                variant="outline"
                onClick={() => {
                  closeModal();
                  openModal("delete", selectedVisitor);
                }}
                className="cht-text-rose-600 hover:cht-bg-rose-50"
                leftIcon={<Trash2 size={16} />}
              >
                Delete Record
              </Button>

              <div className="cht-flex cht-items-center cht-gap-2">
                {selectedVisitor.status !== "CHECKED_IN" && (
                  <Button
                    variant="outline"
                    onClick={() => handleCheckIn(selectedVisitor.id)}
                    leftIcon={<CheckCircle2 size={16} className="cht-text-emerald-600" />}
                  >
                    Check In
                  </Button>
                )}

                {selectedVisitor.status !== "CHECKED_OUT" && (
                  <Button
                    variant="primary"
                    onClick={() => handleCheckOut(selectedVisitor.id)}
                    leftIcon={<LogOut size={16} />}
                  >
                    Check Out
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal: Delete Confirmation */}
      <Modal
        isOpen={isModalOpen && modalMode === "delete"}
        onClose={closeModal}
        title="Confirm Record Deletion"
      >
        <div className="cht-space-y-5">
          <div className="cht-p-4 cht-rounded-2xl cht-bg-rose-50 cht-border cht-border-rose-100 cht-flex cht-items-start cht-gap-3">
            <AlertTriangle className="cht-text-rose-600 cht-shrink-0 cht-mt-0.5" size={20} />
            <div>
              <h4 className="cht-text-sm cht-font-black cht-text-rose-800">
                Permanently Delete Visitor Record?
              </h4>
              <p className="cht-text-xs cht-text-rose-700 cht-mt-1">
                This will completely remove the visitor entry for{" "}
                <strong>{selectedVisitor?.full_name}</strong> (ID: {selectedVisitor?.visitor_id || `#${selectedVisitor?.id}`})
                from the master facility database. This action cannot be undone.
              </p>
            </div>
          </div>

          <div className="cht-flex cht-items-center cht-justify-end cht-gap-3 cht-pt-2">
            <Button variant="ghost" type="button" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              variant="outline"
              className="cht-bg-rose-600 cht-text-white hover:cht-bg-rose-700 cht-border-transparent"
              onClick={handleDeleteVisitor}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Deleting..." : "Yes, Delete Record"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SuperAdminVisitors;
